import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

interface HeaderItem {
  title: string;
  background?: string;
  count?: number;
  hue: number;
  saturation: number;
  lightness: number;
}

interface AppItem {
  title: string;
  filter: HeaderItem[];
  icon: string;
  packs: boolean[];
  video?: string;
}

interface SectionItem {
  title: string;
  packs: boolean[];
  icon: string;
}

@Injectable({
  providedIn: 'root',
})
export class AppsService {
  private headers: HeaderItem[] = [
    {
      title: this.translate.instant('applications.headers.all'),
      background: '#ffffff',
      hue: 0,
      saturation: 0,
      lightness: 100,
    },
    {
      title: this.translate.instant('applications.headers.branding'),
      background: '#400e9e',
      hue: 253,
      saturation: 91,
      lightness: 35,
    },
    {
      title: this.translate.instant('applications.headers.conversion'),
      background: '#2462d4',
      hue: 211,
      saturation: 78,
      lightness: 39,
    },
    {
      title: this.translate.instant('applications.headers.ux'),
      background: '#b9632a',
      hue: 27,
      saturation: 70,
      lightness: 41,
    },
    {
      title: this.translate.instant('applications.headers.order_tracker'),
      background: '#bd2525',
      hue: 5,
      saturation: 74,
      lightness: 44,
    },
    {
      title: this.translate.instant('applications.headers.security'),
      background: '#649b22',
      hue: 92,
      saturation: 56,
      lightness: 37,
    },
  ];

  private apps: AppItem[] = [
    {
      title: this.translate.instant(
        'applications.contents.quick_product_display'
      ),
      filter: [this.headers[0], this.headers[3]],
      icon: 'fa-regular fa-magnifying-glass',
      video: '7n_szbC0N-4',
      packs: [false, true, true],
    },
    {
      title: this.translate.instant('applications.contents.cart_animation'),
      filter: [this.headers[0], this.headers[2]],
      icon: 'fa-regular fa-cart-shopping-fast',
      video: 'J6fASyVsxhA',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.delivery_date_announcement'
      ),
      filter: [this.headers[0], this.headers[2]],
      icon: 'fa-regular fa-truck-clock',
      video: 'OX0TYamlwh4',
      packs: [false, false, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.main_review_product_page'
      ),
      filter: [this.headers[0], this.headers[2]],
      icon: 'fa-regular fa-star',
      video: '915MVdr4WiQ',
      packs: [false, false, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.fixed_add_bar_product_page'
      ),
      filter: [this.headers[0], this.headers[2]],
      icon: 'fa-regular fa-tape',
      video: 'sOCanY6KyeI',
      packs: [false, true, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.product_bundle_product_page'
      ),
      filter: [this.headers[0], this.headers[5]],
      icon: 'fa-regular fa-basket-shopping-simple',
      video: '8I0E-_xvaD0',
      packs: [false, false, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.promotion_countdown'
      ),
      filter: [this.headers[0], this.headers[2]],
      icon: 'fa-regular fa-stopwatch-20',
      video: 'PksFJBbLbq8',
      packs: [false, true, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.asos_style_client_area'
      ),
      filter: [this.headers[0], this.headers[3], this.headers[1]],
      icon: 'fa-regular fa-user',
      video: 'LeTs-04IPgE',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant('applications.contents.faq'),
      filter: [this.headers[0], this.headers[3]],
      icon: 'fa-regular fa-circle-question',
      video: '2YBNsopUjEs',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.advanced_collection_page_filtering'
      ),
      filter: [this.headers[0], this.headers[3]],
      icon: 'fa-regular fa-filters',
      video: 'Fi_EYeI22Xw',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.blog_page_tag_filtering'
      ),
      filter: [this.headers[0], this.headers[3]],
      icon: 'fa-regular fa-filter-list',
      video: 'ak13wQoz9LE',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.google_maps_card_section'
      ),
      filter: [this.headers[0], this.headers[3]],
      icon: 'fa-regular fa-map-pin',
      video: 'kQBJMBMDwUQ',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant('applications.contents.font_awesome_icons'),
      filter: [this.headers[0], this.headers[1]],
      icon: 'fa-regular fa-flag',
      video: 'O3v4zpYs2L8',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.instagram_feed_social_section'
      ),
      filter: [this.headers[0], this.headers[1]],
      icon: 'fa-brands fa-instagram',
      video: 'sR9bR6BRJsw',
      packs: [false, false, true],
    },
    {
      title: this.translate.instant('applications.contents.favorites_list'),
      filter: [this.headers[0], this.headers[3], this.headers[1]],
      icon: 'fa-regular fa-heart',
      video: 'kJtQjLz1Qkk',
      packs: [false, false, true],
    },
    {
      title: this.translate.instant('applications.contents.multi_currency'),
      filter: [this.headers[0], this.headers[3]],
      icon: 'fa-regular fa-euro-sign',
      video: 'XkoWoAqT9i4',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.hide_prices_product_card'
      ),
      filter: [this.headers[0], this.headers[1]],
      icon: 'fa-regular fa-money-bill',
      video: 'g4dCFYZXcqM',
      packs: [false, true, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.sales_notifications'
      ),
      filter: [this.headers[0], this.headers[2]],
      icon: 'fa-regular fa-bell-on',
      video: 'fuDgbXRyxkQ',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant('applications.contents.modular_megamenu'),
      filter: [this.headers[0], this.headers[3]],
      icon: 'fa-regular fa-ellipsis-vertical',
      video: 'WuxL0zeYPVM',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant('applications.contents.cart_goal'),
      filter: [this.headers[0], this.headers[5]],
      icon: 'fa-regular fa-bullseye-arrow',
      video: '8JQfzSGzxE8',
      packs: [false, false, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.infinite_pagination'
      ),
      filter: [this.headers[0], this.headers[3]],
      icon: 'fa-regular fa-infinity',
      video: 'QhR9q1s1-SA',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.pinterest_feed_social_section'
      ),
      filter: [this.headers[0], this.headers[1]],
      icon: 'fa-brands fa-pinterest',
      video: 'L-HpXn_dztk',
      packs: [false, false, true],
    },
    {
      title: this.translate.instant('applications.contents.size_guide_popup'),
      filter: [this.headers[0], this.headers[3]],
      icon: 'fa-regular fa-ruler-combined',
      video: 'q8bT0sCISfw',
      packs: [false, true, true],
    },
    {
      title: this.translate.instant('applications.contents.newsletter_popup'),
      filter: [this.headers[0], this.headers[2]],
      icon: 'fa-regular fa-inbox',
      video: 'yBjReKUQkkk',
      packs: [false, true, true],
    },
    {
      title: this.translate.instant('applications.contents.spin_popup'),
      filter: [this.headers[0], this.headers[2], this.headers[5]],
      icon: 'fa-regular fa-dharmachakra',
      video: 'zVu3-IdezDw',
      packs: [false, false, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.age_verification_popup'
      ),
      filter: [this.headers[0], this.headers[4]],
      icon: 'fa-regular fa-ban',
      video: 'p9oIe6-GY1Y',
      packs: [false, true, true],
    },
    {
      title: this.translate.instant('applications.contents.aos_animation'),
      filter: [this.headers[0], this.headers[3]],
      icon: 'fa-regular fa-sparkles',
      video: 'vyOEGTKxlF4',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant('applications.contents.content_protection'),
      filter: [this.headers[0], this.headers[4]],
      icon: 'fa-regular fa-lock',
      video: '_gzKragPnnw',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant('applications.contents.gdpr'),
      filter: [this.headers[0], this.headers[4], this.headers[3]],
      icon: 'fa-regular fa-shield',
      video: 'ni55mbs5WUA',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant('applications.contents.cart_reservation'),
      filter: [this.headers[0], this.headers[2]],
      icon: 'fa-regular fa-chess-clock',
      video: 'yIREVxgnGUM',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant('applications.contents.swiper_slideshow'),
      filter: [this.headers[0], this.headers[3]],
      icon: 'fa-regular fa-presentation-screen',
      video: 'v90GekHx5_E',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant('applications.contents.tracking_genie'),
      filter: [this.headers[0], this.headers[5]],
      icon: 'fa-regular fa-box-check',
      video: 'qTVfV6s3CTI',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant('applications.contents.tracking_more'),
      filter: [this.headers[0], this.headers[5]],
      icon: 'fa-regular fa-truck-fast',
      video: 'DWHTDBH8gms',
      packs: [false, true, true],
    },
    {
      title: this.translate.instant('applications.contents.parcel_apps'),
      filter: [this.headers[0], this.headers[5]],
      icon: 'fa-regular fa-truck-fast',
      video: '',
      packs: [false, false, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.twitter_social_feed_section'
      ),
      filter: [this.headers[0], this.headers[1]],
      icon: 'fa-brands fa-twitter',
      video: 'EOeL5XmywD8',
      packs: [false, false, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.upsell_mini_cart_cart_page'
      ),
      filter: [this.headers[0], this.headers[5]],
      icon: 'fa-regular fa-cart-plus',
      video: '1Ev2P1ZcVyA',
      packs: [false, false, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.upsell_product_page'
      ),
      filter: [this.headers[0], this.headers[5]],
      icon: 'fa-regular fa-cowbell-circle-plus',
      video: '7hrcjQR3BuY',
      packs: [false, false, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.product_badges_block'
      ),
      filter: [this.headers[0], this.headers[2]],
      icon: 'fa-regular fa-brake-warning',
      video: 'vzieObf22m0',
      packs: [true, true, true],
    },
    {
      title: this.translate.instant('applications.contents.color_thumbnails'),
      filter: [this.headers[0], this.headers[3]],
      icon: 'fa-regular fa-swatchbook',
      video: '9rGEnU9jCro',
      packs: [false, false, true],
    },
    {
      title: this.translate.instant(
        'applications.contents.quick_add_to_cart_product_card'
      ),
      filter: [this.headers[0], this.headers[2]],
      icon: 'fa-regular fa-bolt',
      packs: [false, false, true],
    },
  ];

  private sections: SectionItem[] = [
    {
      title: this.translate.instant('sections.applications'),
      packs: [true, true, true],
      icon: 'fa-brands fa-shopify',
    },
    {
      title: this.translate.instant('sections.blog_article_1'),
      packs: [true, true, true],
      icon: 'fa-regular fa-rss',
    },
    {
      title: this.translate.instant('sections.blog_article_2'),
      packs: [true, true, true],
      icon: 'fa-regular fa-rss',
    },
    {
      title: this.translate.instant('sections.blog_article_3'),
      packs: [true, true, true],
      icon: 'fa-regular fa-rss',
    },
    {
      title: this.translate.instant('sections.map'),
      packs: [true, true, true],
      icon: 'fa-solid fa-map-pin',
    },
    {
      title: this.translate.instant('sections.custom_html_code'),
      packs: [true, true, true],
      icon: 'fa-regular fa-code',
    },
    {
      title: this.translate.instant('sections.featured_collection_1'),
      packs: [true, true, true],
      icon: 'fa-regular fa-line-columns',
    },
    {
      title: this.translate.instant('sections.featured_collection_2'),
      packs: [true, true, true],
      icon: 'fa-regular fa-line-columns',
    },
    {
      title: this.translate.instant('sections.alternating_columns'),
      packs: [true, true, true],
      icon: 'fa-regular fa-image',
    },
    {
      title: this.translate.instant('sections.image_columns_with_text'),
      packs: [true, true, true],
      icon: 'fa-regular fa-image',
    },
    {
      title: this.translate.instant('sections.slideshow'),
      packs: [true, true, true],
      icon: 'fa-regular fa-images',
    },
    {
      title: this.translate.instant('sections.slide'),
      packs: [true, true, true],
      icon: 'fa-regular fa-image',
    },

    {
      title: this.translate.instant('sections.section_header'),
      packs: [true, true, true],
      icon: 'fa-regular fa-align-left',
    },
    {
      title: this.translate.instant('sections.faq'),
      packs: [true, true, true],
      icon: 'fa-regular fa-question',
    },
    {
      title: this.translate.instant('sections.gallery'),
      packs: [true, true, true],
      icon: 'fa-regular fa-images',
    },
    {
      title: this.translate.instant('sections.featured_images'),
      packs: [true, true, true],
      icon: 'fa-regular fa-images',
    },
    {
      title: this.translate.instant('sections.collection_list_1'),
      packs: [true, true, true],
      icon: 'fa-regular fa-rectangle-list',
    },
    {
      title: this.translate.instant('sections.collection_list_2'),
      packs: [true, true, true],
      icon: 'fa-regular fa-rectangle-list',
    },
    {
      title: this.translate.instant('sections.media_with_text'),
      packs: [true, true, true],
      icon: 'fa-regular fa-images',
    },
    {
      title: this.translate.instant('sections.newsletter_1'),
      packs: [true, true, true],
      icon: 'fa-regular fa-inbox',
    },
    {
      title: this.translate.instant('sections.featured_product'),
      packs: [true, true, true],
      icon: 'fa-regular fa-fire',
    },
    {
      title: this.translate.instant('sections.testimonials_1'),
      packs: [true, true, true],
      icon: 'fa-regular fa-stars',
    },
    {
      title: this.translate.instant('sections.video'),
      packs: [true, true, true],
      icon: 'fa-regular fa-video',
    },
    {
      title: this.translate.instant('sections.videos'),
      packs: [true, true, true],
      icon: 'fa-regular fa-video',
    },
    {
      title: this.translate.instant('sections.trust_badges'),
      packs: [false, true, true],
      icon: 'fa-regular fa-shield-check',
    },
    {
      title: this.translate.instant('sections.featured_collection_3'),
      packs: [false, true, true],
      icon: 'fa-regular fa-line-columns',
    },
    {
      title: this.translate.instant('sections.promotions'),
      packs: [false, true, true],
      icon: 'fa-regular fa-percent',
    },
    {
      title: this.translate.instant('sections.vertical_tabs'),
      packs: [false, true, true],
      icon: 'fa-regular fa-rows',
    },
    {
      title: this.translate.instant('sections.testimonials_2'),
      packs: [false, true, true],
      icon: 'fa-regular fa-stars',
    },
    {
      title: this.translate.instant('sections.instagram_shopping_feed'),
      packs: [false, false, true],
      icon: 'fa-brands fa-instagram',
    },
    {
      title: this.translate.instant('sections.featured_boxes'),
      packs: [false, false, true],
      icon: 'fa-regular fa-table-layout',
    },
    {
      title: this.translate.instant('sections.collage'),
      packs: [false, false, true],
      icon: 'fa-regular fa-table-cells',
    },
    {
      title: this.translate.instant('sections.social_feed'),
      packs: [false, false, true],
      icon: 'fa-regular fa-table-cells',
    },
    {
      title: this.translate.instant('sections.collection_list_3'),
      packs: [false, false, true],
      icon: 'fa-regular fa-rectangle-list',
    },
    {
      title: this.translate.instant('sections.logo_list'),
      packs: [false, false, true],
      icon: 'fa-regular fa-ellipsis-stroke',
    },
    {
      title: this.translate.instant('sections.newsletter_2'),
      packs: [false, false, true],
      icon: 'fa-regular fa-inbox',
    },
    {
      title: this.translate.instant('sections.horizontal_tabs'),
      packs: [false, false, true],
      icon: 'fa-regular fa-table-layout',
    },
  ];

  constructor(private translate: TranslateService) {}

  getFilters() {
    return this.headers;
  }

  getApps() {
    return this.apps;
  }

  getSections() {
    return this.sections;
  }
}
